/**
 * Display-only purchase breakdown.
 * Reads the same package matrix the amount step already quotes.
 * Does not price a payment, mint shares, or change a sponsor gift.
 */
import { PRODUCTION_CAPITAL_CAMPAIGN, quoteProductionCapitalCart } from './productionCapitalCampaign.js';
import { BIRTHDAY_PROMO_CONFIG, computeBirthdayBreakdown, isBirthdayPackageId } from './birthdayPromo.js';
import { computeSingleShareSpecialBreakdown } from './singleShareSpecial.js';

export function formatShareCount(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '0';
  return String(Number(n.toFixed(4)));
}

function line(label, paidShares, buyerBonus, sponsorBonus) {
  return {
    label,
    paidShares,
    buyerBonus,
    sponsorBonus,
  };
}

/**
 * @param {{
 *   packages?: Array<{ packageId?: string, qty?: number }>,
 *   phasePrice?: number,
 *   birthdayPackage?: string,
 *   birthdayQty?: number,
 *   paidShares?: number,
 *   tierBonusShares?: number,
 *   singleShareActive?: boolean,
 * }} input
 * @returns {{
 *   source: 'pcap' | 'birthday' | 'single' | 'tier',
 *   packages: Array<{ label: string, paidShares: number, buyerBonus: number, sponsorBonus: number }>,
 *   paidShares: number,
 *   buyerBonus: number,
 *   buyerTotal: number,
 *   sponsorBonus: number | null,
 * } | null}
 */
export function buildPurchaseRewardPreview(input) {
  const src = input && typeof input === 'object' ? input : {};
  const packages = Array.isArray(src.packages)
    ? src.packages.filter((row) => row && Number(row.qty) > 0)
    : [];
  const phasePrice = Number(src.phasePrice) > 0
    ? Number(src.phasePrice)
    : PRODUCTION_CAPITAL_CAMPAIGN.pricePerPaidShareUsd;

  if (packages.length) {
    const cart = quoteProductionCapitalCart(packages, phasePrice);
    if (!cart) return null;
    return {
      source: 'pcap',
      packages: cart.lines.map((row) => line(
        `${row.name} ×${row.qty}`,
        row.paidShares,
        row.investorBonus,
        row.sponsorBonus,
      )),
      paidShares: cart.paidShares,
      buyerBonus: cart.investorBonus,
      buyerTotal: cart.investorTotal,
      sponsorBonus: cart.sponsorBonus,
    };
  }

  if (isBirthdayPackageId(src.birthdayPackage)) {
    const birthday = computeBirthdayBreakdown(src.birthdayPackage, src.birthdayQty || 1);
    if (!birthday) return null;
    const pkg = BIRTHDAY_PROMO_CONFIG.packages[birthday.packageId];
    return {
      source: 'birthday',
      packages: [line(
        `${pkg.label} ×${birthday.qty}`,
        birthday.baseShares,
        birthday.buyerBonus,
        birthday.uplineBonus,
      )],
      paidShares: birthday.baseShares,
      buyerBonus: birthday.buyerBonus,
      buyerTotal: birthday.baseShares + birthday.buyerBonus,
      sponsorBonus: birthday.uplineBonus,
    };
  }

  if (src.singleShareActive) {
    const special = computeSingleShareSpecialBreakdown(src.paidShares);
    if (special && special.buyerBonus > 0) {
      return {
        source: 'single',
        packages: [],
        paidShares: special.paidShares,
        buyerBonus: special.buyerBonus,
        buyerTotal: special.paidShares + special.buyerBonus,
        sponsorBonus: special.uplineBonus,
      };
    }
  }

  const tierBonus = Number(src.tierBonusShares);
  const paidShares = Number(src.paidShares);
  if (Number.isFinite(tierBonus) && tierBonus > 0 && Number.isFinite(paidShares) && paidShares > 0) {
    return {
      source: 'tier',
      packages: [],
      paidShares,
      buyerBonus: tierBonus,
      buyerTotal: paidShares + tierBonus,
      sponsorBonus: null,
    };
  }

  return null;
}
